import { Availability, offsetByTZ, unparseAvailability } from "@caredna-marlin/utils/client/availability";
import { AvailabilityBlockTypeEnum, AvailabilityProviderTravelEnum, AvailabilityRruleFreqEnum, AvailabilityTypeEnum, AvailTzHandling, useApplyAvailabilityTemplateMutation, useCreateAvailabilityMutation, useCreateRecurringCalendarBlockMutation, useDeleteAvailabilityMutation, useUpdateAvailabilityMutation, useUpdateRecurringCalendarBlockMutation } from "~/graphql/__generated__/graphql";
import { gql } from "@apollo/client";
import { startOfToday } from "date-fns";
import { unknownType } from "./visitDragDropUtils";

export type AvailabilityAction = "create" | "edit" | "copy" | "delete";
export type MulipleAvailabilityAction = "template"

const unparse = unparseAvailability<AvailabilityTypeEnum, AvailabilityProviderTravelEnum, AvailabilityBlockTypeEnum, AvailabilityRruleFreqEnum>;

const refetchQueries = ["AvailabilityByTimespan", "CommandIndexCombinedData", "HomeHealthDemoHorizon"];
const awaitRefetchQueries = true;

const useAvailabilityOperation = () => {
  const [createAvailability, createStatus] = useCreateAvailabilityMutation();
  const [createRecurringBlock, createBlockStatus] = useCreateRecurringCalendarBlockMutation();
  const [updateAvailability, updateStatus] = useUpdateAvailabilityMutation();
  const [updateRecurringBlock, updateBlockStatus] = useUpdateRecurringCalendarBlockMutation();
  const [deleteAvailability, deleteStatus] = useDeleteAvailabilityMutation();
  const [applyTemplate, templateStatus] = useApplyAvailabilityTemplateMutation();

  const loading = createStatus.loading || createBlockStatus.loading || updateStatus.loading || updateBlockStatus.loading || deleteStatus.loading || templateStatus.loading;

  const onSave = (
    action: AvailabilityAction,
    availability: Availability,
    additionalProviderIds: number[],
    additionalAvailabilityIds: number[],
    tzHandling: AvailTzHandling,
    providerTzs: Record<string, string>,
  ) => {
    let avail = unparse(availability, availability.providerId);

    try {
      switch (action) {
        // Intentional fall-through; a "copy" operation is "create" with a little massaging.
        // @ts-expect-error
        case "copy": {

          // To copy an existing availability, we create it for all the providers selected.
          // The first one goes in `providerId` (replacing the existing value, the provider we're copying from),
          // and the rest remain in `additionalProviderIds`.
          additionalProviderIds = [...additionalProviderIds];
          const first = additionalProviderIds.shift();
          if (!first) { return; }

          // If we're using a provider in a different timezone as a "basis" for the create operation
          // and we want to preserve calendar time rather than "user's local time",
          // then we need to adjust the values we pass in to use the new "base provider"'s timezone.
          if (tzHandling === AvailTzHandling.UseCalendarTime && providerTzs[first] !== providerTzs[avail.providerId]) {
            avail = offsetByTZ(avail, providerTzs[avail.providerId], providerTzs[first])
          }

          avail.providerId = first;
        }

        // falls through
        case "create": {
          const variables = {
            ...avail,
            id: undefined,
            additionalProviderIds,
            tzHandling,
          };

          const query = variables.type === AvailabilityTypeEnum.CalendarBlock ? createRecurringBlock : createAvailability;
          return query({ variables, awaitRefetchQueries, refetchQueries });
        }

        case "edit": {
          const variables = {
            ...avail,
            additionalIds: additionalAvailabilityIds,
            tzHandling,
          };
          const query = variables.type === AvailabilityTypeEnum.CalendarBlock ? updateRecurringBlock : updateAvailability;
          return query({ variables, awaitRefetchQueries, refetchQueries });
        }

        case "delete": {
          const variables = {
            id: avail.id,
            providerId: avail.providerId,
            additionalIds: additionalAvailabilityIds,
          };

          return deleteAvailability({ variables, awaitRefetchQueries, refetchQueries })
        }

        default: return unknownType(action);
      }
    } catch (e) {
      // Do nothing; display is already handled elsewhere.
      console.error(e);
    }
  };

  const onSaveTemplate = (
    availabilityIds: number[],
    providerIds: number[],
    tzHandling: AvailTzHandling,
    doNotCommit?: true,
  ) => {
    try {
      const variables = {
        availabilityIds,
        providerIds,
        todayDate: startOfToday().toISOString(),
        tzHandling,
        doNotCommit: doNotCommit ?? null,
      }
      return applyTemplate({ variables, awaitRefetchQueries, refetchQueries })
    } catch (e) {
      // Do nothing; display is already handled elsewhere.
      console.error(e);
    }
  }

  return {
    loading,
    onSave,
    onSaveTemplate,
  };
}

export default useAvailabilityOperation;

gql(`
  mutation CreateAvailability(
    $providerId: Int!,
    $additionalProviderIds: [Int!]
    $tzHandling: AvailTZHandling!,
    $type: AvailabilityTypeEnum!,
    $note: String!,
    $startTime: String!,
    $endTime: String!,
    $providerTravel: AvailabilityProviderTravelEnum,
    $shiftCenterId: Int,
    $overflowShift: Boolean!,
    $useShiftCenterDriveTimes: Boolean!,
    $rruleFreq: AvailabilityRruleFreqEnum!,
    $rruleDtstart: String!,
    $rruleInterval: Int,
    $rruleByweekday: String,
    $rruleUntil: String,
  )
  @permission(requires: manageProviderShifts)
  {
    createAvailability(
      providerId: $providerId
      additionalProviderIds: $additionalProviderIds
      tzHandling: $tzHandling
      type: $type
      note: $note
      startTime: $startTime
      endTime: $endTime
      providerTravel: $providerTravel
      shiftCenterId: $shiftCenterId
      overflowShift: $overflowShift
      useShiftCenterDriveTimes: $useShiftCenterDriveTimes
      rruleFreq: $rruleFreq
      rruleDtstart: $rruleDtstart
      rruleInterval: $rruleInterval
      rruleByweekday: $rruleByweekday
      rruleUntil: $rruleUntil
    ) { id }
  }
`);

gql(`
  mutation UpdateAvailability(
    $id: Int!,
    $additionalIds: [Int!],
    $providerId: Int!,
    $note: String!,
    $startTime: String!,
    $endTime: String!,
    $providerTravel: AvailabilityProviderTravelEnum,
    $shiftCenterId: Int,
    $tzHandling: AvailTZHandling!,
    $overflowShift: Boolean!,
    $useShiftCenterDriveTimes: Boolean!,
    $rruleFreq: AvailabilityRruleFreqEnum!,
    $rruleDtstart: String!,
    $rruleInterval: Int,
    $rruleByweekday: String,
    $rruleUntil: String
  )
  @permission(requires: manageProviderShifts)
  {
    updateAvailability(
      id: $id
      additionalIds: $additionalIds
      tzHandling: $tzHandling
      providerId: $providerId
      note: $note
      providerTravel: $providerTravel
      shiftCenterId: $shiftCenterId
      overflowShift: $overflowShift
      useShiftCenterDriveTimes: $useShiftCenterDriveTimes
      startTime: $startTime
      endTime: $endTime
      rruleFreq: $rruleFreq
      rruleDtstart: $rruleDtstart
      rruleInterval: $rruleInterval
      rruleByweekday: $rruleByweekday
      rruleUntil: $rruleUntil
    ) {
      id
      type
      note
      providerTravel
      shiftCenterId
      overflowShift
      useShiftCenterDriveTimes
      startTime
      endTime
      rruleFreq
      rruleDtstart
      rruleInterval
      rruleByweekday
      rruleUntil
    }
  }
`);

gql(`
  mutation CreateRecurringCalendarBlock(
    $providerId: Int!
    $additionalProviderIds: [Int!]
    $tzHandling: AvailTZHandling!,
    $type: AvailabilityTypeEnum!
    $note: String!
    $startTime: String!
    $endTime: String!
    $blockType: AvailabilityBlockTypeEnum!
    $blockFilters: CalendarBlockFilterInput
    $addressStreet: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $rruleFreq: AvailabilityRruleFreqEnum!
    $rruleDtstart: String!
    $rruleInterval: Int
    $rruleByweekday: String
    $rruleUntil: String
  )
  @permission(requires: manageProviderShifts)
  {
    createAvailability(
      providerId: $providerId
      additionalProviderIds: $additionalProviderIds
      tzHandling: $tzHandling
      type: $type
      note: $note
      startTime: $startTime
      endTime: $endTime

      blockType: $blockType
      blockFilters: $blockFilters
      addressStreet: $addressStreet
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip

      rruleFreq: $rruleFreq
      rruleDtstart: $rruleDtstart
      rruleInterval: $rruleInterval
      rruleByweekday: $rruleByweekday
      rruleUntil: $rruleUntil
    ) { id }
  }
`);


gql(`
  mutation UpdateRecurringCalendarBlock(
    $id: Int!,
    $additionalIds: [Int!],
    $tzHandling: AvailTZHandling!,
    $providerId: Int!,
    $note: String!,
    $startTime: String!,
    $endTime: String!,
    $blockType: AvailabilityBlockTypeEnum!
    $blockFilters: CalendarBlockFilterInput
    $addressStreet: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $rruleFreq: AvailabilityRruleFreqEnum!,
    $rruleDtstart: String!,
    $rruleInterval: Int,
    $rruleByweekday: String,
    $rruleUntil: String
  )
  @permission(requires: manageProviderShifts)
  {
    updateAvailability(
      id: $id
      additionalIds: $additionalIds
      tzHandling: $tzHandling
      providerId: $providerId
      note: $note
      startTime: $startTime
      endTime: $endTime

      blockType: $blockType
      blockFilters: $blockFilters
      addressStreet: $addressStreet
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip

      rruleFreq: $rruleFreq
      rruleDtstart: $rruleDtstart
      rruleInterval: $rruleInterval
      rruleByweekday: $rruleByweekday
      rruleUntil: $rruleUntil
    ) {
      id
      type
      note
      startTime
      endTime

      blockType
      blockFilters {
        visitTypeIds
      }
      addressStreet
      addressCity
      addressState
      addressZip
      addressTimezone
      addressCoordinates

      rruleFreq
      rruleDtstart
      rruleInterval
      rruleByweekday
      rruleUntil
    }
  }
`);

gql(`
  mutation DeleteAvailability(
    $id: Int!,
    $additionalIds: [Int!],
    $providerId: Int!
  )
  @permission(requires: manageProviderShifts)
  {
    deleteAvailability(
      id: $id,
      additionalIds: $additionalIds,
      providerId: $providerId
    ) { id }
  }
`);

gql(`
  mutation ApplyAvailabilityTemplate(
    $availabilityIds: [Int!]!,
    $providerIds: [Int!]!,
    $todayDate: String!
    $doNotCommit: Boolean,
    $tzHandling: AvailTZHandling!
  )
  @permission(requires: manageProviderShifts)
  {
    applyAvailabilityTemplate(
      availabilityIds: $availabilityIds,
      providerIds: $providerIds
      todayDate: $todayDate
      doNotCommit: $doNotCommit
      tzHandling: $tzHandling
    ) { id }
  }
`);
